// src/components/layout/MobileNav.jsx
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home,
  Compass,
  BookOpen,
  Sparkles,
  MessageSquare,
  MessageCircle,
  LogOut,
  Settings,
  Award,
  LogIn,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { noteCompanionEntry } from '../../services/coachModuleContext';

const MobileNav = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logOut, redirectToAuth } = useAuth();

  const navigation = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Assessment', href: '/assessment', icon: Compass },
    { name: 'Dive In', href: '/modules', icon: BookOpen },
    { name: 'Coach', href: '/coach', icon: MessageCircle },
    { name: 'Your Lab', href: '/toolkit', icon: Sparkles },
    { name: 'Dashboard', href: '/dashboard', icon: Award },
    { name: 'Resources', href: '/resources', icon: MessageSquare },
  ];

  const handleLogout = async () => {
    try {
      if (await logOut()) return; // on the way to the hub's sign-out
      navigate('/');
      onClose();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
        onClick={onClose}
      />

      {/* Slide-out Menu */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 20 }}
        className="fixed right-0 top-0 bottom-0 w-80 bg-slate-800 shadow-2xl z-50 lg:hidden overflow-y-auto"
      >
        <div className="p-6">
          {/* User Info */}
          {user && (
            <div className="mb-6 p-4 bg-gold/20 rounded-lg">
              <p className="text-pearl font-medium">{user.displayName || 'User'}</p>
              <p className="text-sm text-muted-gold">{user.email}</p>
            </div>
          )}

          {/* Navigation Links */}
          <nav className="space-y-2 mb-6">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => {
                    if (item.href === '/coach') noteCompanionEntry();
                    onClose();
                  }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-gold/20 text-bright-gold'
                      : 'text-pearl hover:text-bright-gold hover:bg-slate-800/20'
                  }`}
                >
                  <Icon className="w-5 h-5" aria-hidden="true" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Actions */}
          {user ? (
            <div className="space-y-2 pt-6 border-t border-slate-900">
              <Link
                to="/privacy?tab=settings"
                onClick={onClose}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-pearl hover:text-bright-gold hover:bg-slate-800/20 transition-all"
              >
                <Settings className="w-5 h-5" aria-hidden="true" />
                <span>Settings</span>
              </Link>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-pearl hover:text-bright-gold hover:bg-slate-800/20 transition-all text-left"
              >
                <LogOut className="w-5 h-5" aria-hidden="true" />
                <span>Log Out</span>
              </button>
            </div>
          ) : (
            <div className="pt-6 border-t border-slate-900">
              <button
                type="button"
                onClick={() => { onClose(); redirectToAuth('signin'); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-gold/20 text-bright-gold hover:bg-gold/30 transition-all font-medium text-left"
              >
                <LogIn className="w-5 h-5" aria-hidden="true" />
                <span>Log In</span>
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default MobileNav;